// Globals
import { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useSearchParams } from 'react-router-dom';

// Store
import { useAppDispatch, useAppState } from '@/store';
import { activateUser, deactivateUser, getUsersList, selectUserFilters, usersSelector } from '../../feature';

// Components
import { useForm } from '@/components';
import UsersList from '../../components/UsersList/UsersList';
import CreateUserContainer from '../CreateUserContainer/CreateUserContainer';
import {
  StyledContentBlock,
  StyledDrawer,
  StyledHeaderBlock,
  StyledPageTitle,
  StyledPageWrapper,
} from './UsersListContainerStyles';

// Models
import { IPaginationDTO } from '@/models/pagination.model';
import { UserStatus } from '@/models/users.model';

// Helpers
import { useBoolean } from '@/hooks';
import { GetUsersListDTO } from '../../dtos';

const UsersListContainer = () => {
  const { t } = useTranslation(['users']);
  const dispatch = useAppDispatch();
  const [params, setSearchParams] = useSearchParams();
  const { value: createFormOpened, setFalse: closeCreateForm, setTrue: openCreateForm } = useBoolean();
  const { isLoading, usersList, userFilters, userDetails } = useAppState(usersSelector);
  const [currentUserId, setCurrentUserId] = useState<string | null>(null);
  const [form] = useForm();

  const handleChangePage = (value: IPaginationDTO) => {
    const dto = new GetUsersListDTO({
      ...userFilters,
      ...value,
    });

    dispatch(getUsersList(dto));
  };

  useEffect(() => {
    const userId = params.get('id');
    if (userId) {
      setCurrentUserId(userId);
      openCreateForm();
    }
  }, []);

  const onDrawerClose = () => {
    form.resetFields();
    setSearchParams(undefined);
    setCurrentUserId(null);
    closeCreateForm();
  };

  const filters = useAppState(selectUserFilters);

  const getUsersListHandler = () => {
    const dto = new GetUsersListDTO({ ...filters });
    dispatch(getUsersList(dto));
  };

  const onUserEdit = (id: string) => {
    setCurrentUserId(id);
    openCreateForm();
  };

  const onUserStatusUpdate = (id: string, status: UserStatus) => {
    const updateAction = status === UserStatus.ACTIVE ? activateUser : deactivateUser;
    dispatch(updateAction(id));
  };

  useEffect(() => {
    getUsersListHandler();
  }, []);

  return (
    <StyledPageWrapper>
      <StyledHeaderBlock>
        <StyledPageTitle>{t('users_title')}</StyledPageTitle>
      </StyledHeaderBlock>
      <StyledContentBlock>
        <UsersList
          isLoading={isLoading}
          onPageChange={handleChangePage}
          onUserEdit={onUserEdit}
          usersList={usersList?.items ?? []}
          updateUserStatus={onUserStatusUpdate}
          pagination={usersList?.pagination ?? {}}
        />
      </StyledContentBlock>
      <StyledDrawer
        open={createFormOpened}
        onClose={onDrawerClose}
        size={'large'}
        title={userDetails?.full_name as string}
      >
        <CreateUserContainer form={form} onFormSubmit={onDrawerClose} id={currentUserId} />;
      </StyledDrawer>
    </StyledPageWrapper>
  );
};

export default UsersListContainer;
